import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle, BadgePercent, Bot, Building2, Check, CheckCircle2, Copy, Crown, Gem, Info, Loader2,
  PlusCircle, Receipt, RefreshCw, Rocket, Sprout, Users, X, XCircle, Zap,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  useBillingMethods, useBillingOrder, useBillingOrders, useBillingPlans, useBillingSummary,
  type BillingOrder, type BillingOrderStatus, type BillingSummary, type PaymentMethodOption, type PricingPlan,
} from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { getBranding } from '@/lib/branding'
import { formatVnd } from '@/lib/format-money'
import { i18n } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import {
  downgradeNote, QUOTA_KEYS, QUOTA_META, addonDeltaText, aiCreditOrderLabel, buildPlans, daysLeft as daysLeftOf, isAddonOrder, isAiCreditOrder, isBlockedDowngrade, isUpgrade, maxDiscount,
  resolvePlan, termLabel, vatBreakdown, vatLabel, vatListNote, vatPercentOf, withoutPromoParam,
  type PlanDef, type ResolvedPlan,
} from './plans'
import { methodShortLabel, parsePaymentReturn, readPendingOrder, writePendingOrder, type PaymentReturnResult } from './payment-methods'
import { InvoiceCell, RequestInvoiceDialog } from './invoice-history'
import { paidSummary } from './order-summary'
import { BankTransferPanel } from './bank-transfer-panel'
import { OrderActions, PendingOrderBanner, useRenewAndPay } from './order-actions'
import { orderActions } from './payment-methods'
import { useAiCredits, useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import {
  amountText, deriveWalletStatus, formatTurns, planAiLimit, PLAN_AI_LABEL, tokenRateOf, unitsToTurns, walletHeadline, WALLET_STATUS_META, type TokenRate,
} from '@/lib/ai-credits'
import { BuyCreditsDialog } from '@/pages/ai/credits/buy-credits-dialog'

/**
 * Gói dịch vụ — kiểu trang phí của SaleSmartly: gói hiện tại + mức dùng, bảng
 * chọn gói, tiện ích bổ sung, lịch sử thanh toán.
 *
 * Thanh toán: owner/admin bấm "Nâng cấp"/"Gia hạn" → chọn phương thức (chỉ hiện phương thức server đã bật —
 * GET /billing/methods; mặc định Chuyển khoản QR) → POST /billing/checkout → tất cả qua EvoPay:
 *  - Thẻ / PayPal: chuyển sang trang cổng → quay về /billing?payment=return&order=…&evopay_status=…
 *  - Chuyển khoản: khung QR ở /billing?payment=bank&order=…
 * Trạng thái thật luôn hỏi server (webhook EvoPay + đối soát), poll 3 giây tới khi hết "chờ".
 * Giá + hạn mức lấy từ server (GET /billing/plans), không tính ở giao diện.
 */
export function BillingPage() {
  const { t } = useTranslation('billing')
  const { data, isLoading, isError } = useBillingSummary()
  const catalog = useBillingPlans()
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.user?.role)
  const canPay = role === 'owner' || role === 'admin'
  const [contactTarget, setContactTarget] = useState<string | null>(null)
  const methods = useBillingMethods()

  if (isLoading || catalog.isLoading) return <Loading />
  if (isError || !data) return <ErrorState message={t('addon.loadError')} />
  if (catalog.isError || !catalog.data) return <ErrorState message={t('upgrade.loadPlansError')} />

  const cfg = catalog.data
  const plans = buildPlans(cfg)
  // Gói trả phí hết hạn → server báo wasDowngraded: hiển thị như gói Miễn phí.
  const current = resolvePlan(data.wasDowngraded ? 'free' : data.plan, cfg.plans)
  const higherPurchasable = plans.find((p) => p.purchasable && isUpgrade(current, p.id, cfg.plans))
  const goUpgrade = (planId: string) => navigate(`/billing/upgrade?plan=${encodeURIComponent(planId)}`)
  const save = maxDiscount(cfg)
  // "Mua thêm" cho kỳ hiện tại: gói trả phí bán trực tuyến, còn hạn.
  const currentDef = current.kind === 'known' ? cfg.plans.find((p) => p.id === current.id) : undefined
  const canAddon = !data.wasDowngraded && !!currentDef?.purchasable && (currentDef.monthlyPrice ?? 0) > 0
    && !!data.expiresAt && new Date(data.expiresAt).getTime() > Date.now()

  return (
    <div className="space-y-6">
      <PageHeader title={t('billingPage.title')} description={t('billingPage.description')} />

      <PaymentReturn />

      {data.wasDowngraded && <DowngradeBanner summary={data} plans={cfg.plans} />}

      <CurrentPlanCard
        summary={data}
        plan={current}
        canRenew={canPay && current.kind === 'known' && !!plans.find((p) => p.id === current.id)?.purchasable}
        onRenew={() => current.id && goUpgrade(current.id)}
        canAddon={canPay && canAddon}
        onAddon={() => navigate('/billing/addon')}
        onUpgrade={() => {
          if (canPay && higherPurchasable) goUpgrade(higherPurchasable.id)
          else setContactTarget(t('billingPage.higherPlan'))
        }}
      />

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-base font-medium">
            <span className="h-4 w-1 rounded-full bg-primary" /> {t('billingPage.choosePlanTitle')}
          </h2>
          {save > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-600 dark:text-orange-400">
              <BadgePercent className="h-3.5 w-3.5" /> {t('billingPage.saveUpTo', { percent: save })}
            </span>
          )}
        </div>
        <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', plans.length >= 5 ? 'xl:grid-cols-3 2xl:grid-cols-5' : plans.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
          {plans.map((p, i) => (
            <PlanCard
              key={p.id}
              plan={p}
              tone={PLAN_TONES[i % PLAN_TONES.length]}
              save={save}
              current={current}
              allPlans={cfg.plans}
              upgradeOnly={!!data.upgradeOnly}
              canPay={canPay}
              onBuy={() => goUpgrade(p.id)}
              onContact={() => setContactTarget(t('billingPage.planNamed', { name: p.name }))}
            />
          ))}
        </div>
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {/* Q2: giá niêm yết chưa VAT — ghi chú VAT lấy theo thuế suất cấu hình; ghi chú cũ nói về VAT thì bỏ để khỏi trùng. */}
          {`${vatListNote(vatPercentOf(cfg))} `}
          {cfg.note && !/VAT/i.test(cfg.note) ? `${cfg.note} ` : ''}
          {paymentNote(methods.data?.methods ?? [])}
          {t('billingPage.limitsDisplayNote')}
        </p>
      </section>

      <AddonsSection summary={data} onBuy={setContactTarget} canAddon={canAddon} canPay={canPay} onAddon={() => navigate('/billing/addon')} />

      <PaymentHistory plans={cfg.plans} canPay={canPay} />

      <ContactDialog target={contactTarget} currentName={current.name} onClose={() => setContactTarget(null)} />
    </div>
  )
}

function paymentNote(methods: PaymentMethodOption[]): string {
  const ids = methods.map((m) => m.id)
  const parts: string[] = []
  if (ids.includes('card')) parts.push(i18n.t('billing:billingPage.paymentNote.card'))
  if (ids.includes('bank')) parts.push(i18n.t('billing:billingPage.paymentNote.bank'))
  if (ids.includes('sepay')) parts.push(i18n.t('billing:billingPage.paymentNote.sepay'))
  if (ids.includes('tingee')) parts.push(i18n.t('billing:billingPage.paymentNote.tingee'))
  if (ids.includes('paypal')) parts.push(i18n.t('billing:billingPage.paymentNote.paypal'))
  return parts.length ? i18n.t('billing:billingPage.paymentNote.prefix', { list: parts.join(i18n.t('billing:billingPage.paymentNote.or')) }) : ''
}

// ── Hạ gói do hết hạn ────────────────────────────────────────────────

function DowngradeBanner({ summary, plans }: { summary: BillingSummary; plans: PricingPlan[] }) {
  const { t } = useTranslation('billing')
  const oldName = resolvePlan(summary.plan, plans).name
  const date = summary.expiresAt ? new Date(summary.expiresAt).toLocaleDateString('vi-VN') : ''
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="space-y-0.5">
        <p className="font-medium">{t('billingPage.planExpired', { name: oldName, dateSuffix: date ? t('billingPage.onDate', { date }) : '' })}</p>
        <p className="text-muted-foreground">
          {t('billingPage.downgradedNote', { name: oldName })}
        </p>
      </div>
    </div>
  )
}

// ── Theo dõi đơn đang chờ (quay về từ cổng thanh toán) ─────────────

const RETURN_HINT: Partial<Record<PaymentReturnResult, string>> = {
  get cancel() { return i18n.t('billing:paymentResult.youCancelledTitle') },
  get error() { return i18n.t('billing:paymentResult.gatewayErrorTitle') },
}

/**
 * Theo dõi đơn: ưu tiên mã đơn trên URL quay về (SePay `order`, ZaloPay `apptransid`),
 * không có thì dùng id đơn lưu trong sessionStorage trước khi rời trang. Trạng thái lấy
 * từ server (IPN SePay / callback ZaloPay / đối soát) — KHÔNG tin `payment=success` trên URL.
 */
export function PaymentReturn({ onPaid }: { onPaid?: () => void } = {}) {
  const { t } = useTranslation('billing')
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const [ret] = useState(() => parsePaymentReturn(params))
  const [trackId, setTrackId] = useState<string | null>(() => ret?.orderRef ?? readPendingOrder())
  // Huỷ / lỗi: chỉ hỏi server một lần (server vẫn đối soát với SePay), không chờ tiếp.
  const poll = ret?.result !== 'cancel' && ret?.result !== 'error'
  const { data: order, isError, refetch } = useBillingOrder(trackId, { poll })
  const renew = useRenewAndPay()
  const [rechecking, setRechecking] = useState(false)
  const settled = !!order && (order.status !== 'pending' || !poll)
  const addonOrder = !!order && isAddonOrder(order)
  const aiOrder = !!order && isAiCreditOrder(order)
  const onPaidRef = useRef(onPaid)
  useEffect(() => { onPaidRef.current = onPaid })

  useEffect(() => {
    if (!settled) return
    writePendingOrder(null)
    qc.invalidateQueries({ queryKey: ['billing-summary'] })
    qc.invalidateQueries({ queryKey: ['billing-orders'] })
    if (aiOrder) qc.invalidateQueries({ queryKey: ['ai', 'credits'] })
    if (order?.status === 'paid') {
      toast.success(aiOrder
        ? t('billingPage.toastPaidAi')
        : addonOrder ? t('billingPage.toastPaidAddon') : t('billingPage.toastPaidPlan'))
      // B9: mã ưu đãi đã dùng cho đơn này → gỡ khỏi URL và ô nhập mã (không áp lại cho đơn sau).
      setParams((prev) => withoutPromoParam(prev) ?? prev, { replace: true })
      onPaidRef.current?.()
    }
  }, [settled, order?.status, addonOrder, aiOrder, qc, setParams, t])

  if (!trackId) return <PendingOrderBanner />

  const gateway = order?.provider === 'zalopay' ? 'ZaloPay'
    : order?.provider === 'evopay_card' ? '2C2P'
    : order?.provider === 'evopay_bank' ? t('paymentResult.gatewayBank')
    : order?.provider === 'evopay_paypal' ? 'PayPal'
    : order?.provider === 'evopay_sepay' ? 'SePay'
    : order?.provider === 'evopay_tingee' ? 'Tingee'
    : 'SePay'
  const close = () => {
    setTrackId(null)
    writePendingOrder(null)
    if (params.has('payment')) {
      setParams((prev) => {
        const n = new URLSearchParams(prev)
        for (const k of ['payment', 'order', 'apptransid', 'evopay_payment_id', 'evopay_order_ref', 'evopay_status', 'evopay_result']) n.delete(k)
        return n
      }, { replace: true })
    }
  }

  let icon = <Loader2 className="h-5 w-5 animate-spin text-primary" />
  let title = t('billingPage.waitingTitle', { gateway })
  let desc = t('billingPage.waitingDesc')
  let tone = 'border-primary/30 bg-primary/5'
  if (isError) {
    icon = <XCircle className="h-5 w-5 text-destructive" />
    title = t('paymentResult.cannotCheckTitle')
    desc = t('billingPage.cannotCheckDesc')
    tone = 'border-destructive/30 bg-destructive/5'
  } else if (order?.status === 'paid') {
    icon = <CheckCircle2 className="h-5 w-5 text-success" />
    ;({ title, desc } = paidSummary(order))
    tone = 'border-success/30 bg-success/5'
  } else if (order?.status === 'underpaid') {
    icon = <AlertTriangle className="h-5 w-5 text-destructive" />
    title = t('paymentResult.underpaidTitle')
    desc = t('billingPage.underpaidDesc', { code: order.paymentCode ?? order.id })
    tone = 'border-destructive/30 bg-destructive/5'
  } else if (order && order.status !== 'pending') {
    icon = <XCircle className="h-5 w-5 text-destructive" />
    title = order.status === 'expired' ? t('paymentResult.expiredTitle') : order.status === 'cancelled' ? t('paymentResult.cancelledTitle') : t('paymentResult.failedTitle')
    desc = t('billingPage.failedDesc')
    tone = 'border-destructive/30 bg-destructive/5'
  } else if (!poll && ret) {
    // Khách huỷ / cổng báo lỗi, đơn vẫn chờ phía server.
    icon = <XCircle className="h-5 w-5 text-destructive" />
    title = RETURN_HINT[ret.result] ?? t('billingPage.notCompletedTitle')
    desc = t('billingPage.notCompletedDesc', { gateway })
    tone = 'border-destructive/30 bg-destructive/5'
  }

  // Chuyển khoản EvoPay còn chờ → hiện QR + thông tin chuyển khoản (vẫn tự hỏi trạng thái đơn).
  if (order?.status === 'pending' && order.bankTransfer && poll) {
    const recheck = async () => {
      setRechecking(true)
      const r = await refetch()
      setRechecking(false)
      if (r.data?.status === 'pending') toast.info(t('paymentResult.bankNotYetReceived'))
    }
    return (
      <BankTransferPanel
        info={order.bankTransfer} expiresAt={order.expiresAt} orderCode={order.paymentCode} onClose={close}
        onRenew={() => renew.run(order.id)} renewing={renew.pending}
        onRecheck={recheck} rechecking={rechecking}
      />
    )
  }

  return (
    <div className={cn('flex items-start gap-3 rounded-lg border p-4 text-sm', tone)}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 space-y-0.5">
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground">{desc}</p>
        {order?.paymentCode && (
          <p className="text-xs text-muted-foreground">{t('billingPage.orderCodeLabel')}: <span className="font-mono">{order.paymentCode}</span></p>
        )}
        {order && order.status !== 'paid' && <div className="pt-2 empty:hidden"><OrderActions order={order} /></div>}
      </div>
      <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground" aria-label={t('common:actions.close')}>
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

// ── Lịch sử thanh toán ───────────────────────────────────────────────

const ORDER_STATUS: Record<BillingOrderStatus, { text: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  get pending() { return { text: i18n.t('billing:orderStatus.pending'), variant: 'warning' as const } },
  get paid() { return { text: i18n.t('billing:orderStatus.paid'), variant: 'success' as const } },
  get failed() { return { text: i18n.t('billing:orderStatus.failed'), variant: 'destructive' as const } },
  get expired() { return { text: i18n.t('billing:orderStatus.expired'), variant: 'secondary' as const } },
  get cancelled() { return { text: i18n.t('billing:orderStatus.cancelled'), variant: 'secondary' as const } },
  get underpaid() { return { text: i18n.t('billing:orderStatus.underpaid'), variant: 'destructive' as const } },
}

function fmtDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' }) : '—'
}

const historyColumns = (plans: PricingPlan[], canPay: boolean, onRequestInvoice: (o: BillingOrder) => void): Column<BillingOrder>[] => [
  { key: 'created', header: i18n.t('billing:billingPage.history.createdAt'), cell: (o) => fmtDateTime(o.createdAt) },
  {
    key: 'plan', header: i18n.t('billing:billingPage.history.plan'), cell: (o) => {
      const q = o.quote
      if (isAiCreditOrder(o)) {
        return <div><Badge variant="secondary" className="mr-1">{i18n.t('billing:billingPage.history.aiTurns')}</Badge>{aiCreditOrderLabel(o) ?? i18n.t('billing:billingPage.history.buyAiTurns')}</div>
      }
      const name = q?.planName ?? resolvePlan(o.planId, plans).name
      if (isAddonOrder(o)) {
        return (
          <div className="min-w-0">
            <div><Badge variant="secondary" className="mr-1">{i18n.t('billing:billingPage.history.addon')}</Badge>{name} · {i18n.t('billing:billingPage.history.termRemaining', { days: o.days })}</div>
            {q?.addon && <div className="truncate text-xs text-muted-foreground">{addonDeltaText(q.addon.delta)}</div>}
          </div>
        )
      }
      const extras = q?.lines ? q.lines.filter((l) => l.extra > 0).map((l) => `+${l.extra} ${l.label.toLowerCase()}`) : []
      const note = q?.mode === 'requantity' ? i18n.t('billing:billingPage.history.requantity') : q?.mode === 'extend' ? i18n.t('billing:billingPage.history.extend') : q?.mode === 'change_plan' ? i18n.t('billing:billingPage.history.changePlan') : null
      return (
        <div className="min-w-0">
          <div>{note && <span className="text-muted-foreground">{note}: </span>}{name} · {termLabel(o.days)}</div>
          {extras.length > 0 && <div className="truncate text-xs text-muted-foreground">{extras.join(', ')}</div>}
        </div>
      )
    },
  },
  { key: 'method', header: i18n.t('billing:billingPage.history.method'), cell: (o) => methodShortLabel(o.provider) },
  {
    key: 'amount', header: i18n.t('billing:billingPage.history.amount'), align: 'right', cell: (o) => {
      // Q2: tổng thanh toán + dòng nhỏ "chưa VAT … + VAT x% …" (đơn cũ không có quote.vat → chỉ tổng).
      const vb = vatBreakdown(o.quote, o.amount)
      return (
        <div className="tabular-nums">
          <div>{formatVnd(o.amount)}</div>
          {vb && vb.amount > 0 && (
            <div className="whitespace-nowrap text-xs text-muted-foreground" title={i18n.t('billing:billingPage.history.vatTitle', { preTax: formatVnd(vb.preTax), vat: vatLabel(vb.percent), amount: formatVnd(vb.amount) })}>
              {formatVnd(vb.preTax)} + {vatLabel(vb.percent)}
            </div>
          )}
        </div>
      )
    },
  },
  {
    key: 'status', header: i18n.t('billing:billingPage.history.status'), cell: (o) => {
      // Đơn chờ nhưng quá hạn theo đồng hồ → hiện "Hết hạn" (server chốt trạng thái chậm một nhịp).
      const s = orderActions(o, Date.now()).expired ? ORDER_STATUS.expired : (ORDER_STATUS[o.status] ?? ORDER_STATUS.pending)
      return (
        <div className="space-y-1.5">
          <Badge variant={s.variant}>{s.text}</Badge>
          <OrderActions order={o} />
        </div>
      )
    },
  },
  { key: 'paid', header: i18n.t('billing:billingPage.history.paidAt'), cell: (o) => fmtDateTime(o.paidAt) },
  {
    key: 'period', header: i18n.t('billing:billingPage.history.period'), cell: (o) => {
      if (!o.periodEnd) return '—'
      const end = new Date(o.periodEnd).toLocaleDateString('vi-VN')
      // Gia hạn nối tiếp: kỳ bắt đầu trong tương lai → hiện cả ngày bắt đầu.
      return o.periodStart && new Date(o.periodStart).getTime() > Date.now()
        ? `${new Date(o.periodStart).toLocaleDateString('vi-VN')} → ${end}`
        : i18n.t('billing:billingPage.history.until', { end })
    },
  },
  {
    key: 'txn', header: i18n.t('billing:billingPage.history.txnCode'), cell: (o) => (
      <span className="font-mono text-xs" title={o.provider !== 'zalopay' && o.providerTxnId ? `SePay #${o.providerTxnId}` : undefined}>
        {o.provider === 'zalopay' ? o.zpTransId ?? '—' : o.paymentCode ?? '—'}
      </span>
    ),
  },
  { key: 'invoice', header: i18n.t('billing:billingPage.history.invoice'), cell: (o) => <InvoiceCell order={o} canPay={canPay} onRequest={onRequestInvoice} /> },
]

function PaymentHistory({ plans, canPay }: { plans: PricingPlan[]; canPay: boolean }) {
  const { t } = useTranslation('billing')
  const { data, isLoading, isError } = useBillingOrders()
  const orders = data?.orders ?? []
  const [invoiceFor, setInvoiceFor] = useState<BillingOrder | null>(null)
  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium">{t('billingPage.paymentHistoryTitle')}</h2>
      {isError ? (
        <ErrorState message={t('billingPage.historyLoadError')} />
      ) : !isLoading && orders.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Receipt}
              title={t('billingPage.noTransactions')}
              description={t('billingPage.noTransactionsDesc')}
            />
          </CardContent>
        </Card>
      ) : (
        <DataTable columns={historyColumns(plans, canPay, setInvoiceFor)} rows={orders} loading={isLoading} rowKey={(o) => o.id} />
      )}
      <RequestInvoiceDialog order={invoiceFor} onClose={() => setInvoiceFor(null)} />
    </section>
  )
}

// ── Gói hiện tại ─────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, { text: string; variant: 'success' | 'warning' | 'destructive' }> = {
  get active() { return { text: i18n.t('billing:billingPage.statusLabel.active'), variant: 'success' as const } },
  get unlimited() { return { text: i18n.t('billing:billingPage.statusLabel.active'), variant: 'success' as const } },
  get expired() { return { text: i18n.t('billing:billingPage.statusLabel.expired'), variant: 'destructive' as const } },
  get suspended() { return { text: i18n.t('billing:billingPage.statusLabel.suspended'), variant: 'destructive' as const } },
}

function expiryText(expiresAt: string | null): { text: string; soon: boolean } {
  if (!expiresAt) return { text: i18n.t('billing:upgrade.noExpiry'), soon: false }
  const d = new Date(expiresAt)
  // Q3: làm tròn LÊN, cùng hàm với trang Mua thêm / server.
  const daysLeft = daysLeftOf(d)
  const date = d.toLocaleDateString('vi-VN')
  if (daysLeft <= 0) return { text: i18n.t('billing:billingPage.expiredOn', { date }), soon: true }
  return { text: i18n.t('billing:billingPage.expiresInDays', { date, days: daysLeft }), soon: daysLeft <= 14 }
}

function CurrentPlanCard({
  summary, plan, canRenew, onRenew, onUpgrade, canAddon, onAddon,
}: {
  summary: BillingSummary; plan: ResolvedPlan; canRenew: boolean; onRenew: () => void; onUpgrade: () => void
  canAddon: boolean; onAddon: () => void
}) {
  const { t } = useTranslation('billing')
  const status = STATUS_LABEL[summary.status] ?? STATUS_LABEL.active
  const expiry = expiryText(summary.expiresAt)
  const { usage } = summary
  const limits = summary.limits
  const canUpgrade = plan.kind === 'known' && plan.id !== 'enterprise'
  // AI hai chế độ: thước theo ví credit (API chưa có → giữ "Lượt AI hôm nay").
  const { data: credits } = useAiCredits()

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardDescription>{t('billingPage.currentPlan')}</CardDescription>
          <div className="flex flex-wrap items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            <CardTitle className="text-xl">{plan.name}</CardTitle>
            <Badge variant={status.variant}>{status.text}</Badge>
            {plan.kind === 'custom' && <Badge variant="secondary">{t('billingPage.customPlan')}</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('billingPage.expiresLabel')}: <span className={cn('text-foreground', expiry.soon && 'font-medium text-destructive')}>{expiry.text}</span>
          </p>
          {summary.addonPurchased && (
            <p className="text-xs text-muted-foreground">{t('billingPage.includesAddonCurrent', { delta: addonDeltaText(summary.addonPurchased) })}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canAddon && <Button variant="outline" onClick={onAddon}><PlusCircle /> {t('billingPage.buyMoreButton')}</Button>}
          {canRenew && <Button variant="outline" onClick={onRenew}><RefreshCw /> {t('addon.renewOrChangeQuantity')}</Button>}
          {canUpgrade && <Button onClick={onUpgrade}><Rocket /> {t('billingPage.upgradePlanButton')}</Button>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <UsageMeter label={t('addon.quotaLabels.members')} used={usage.members} limit={limits.members} />
          <UsageMeter label={t('quotaMeta.officialChannels.label')} hint={t('billingPage.officialChannelsHint')} used={usage.channelsOfficial} limit={limits.officialChannels} />
          <UsageMeter label={t('quotaMeta.personalChannels.label')} hint={t('billingPage.personalChannelsHint')} used={usage.channelsPersonal} limit={limits.personalChannels} />
          <UsageMeter
            label={t('billingPage.activeContactsLabel')}
            hint={t('billingPage.activeContactsHint', { total: formatNumber(usage.contacts) })}
            used={usage.activeContacts30d}
            limit={limits.activeContactsPerMonth}
          />
          {credits ? (
            <UsageMeter
              label={t('billingPage.aiUsedThisMonth')}
              hint={t('billingPage.aiGrantHint', { amount: amountText(credits.monthlyGrant, tokenRateOf(credits)), remaining: walletHeadline(credits).main })}
              used={unitsToTurns(credits.usedThisMonth, tokenRateOf(credits))}
              limit={credits.monthlyGrant ? unitsToTurns(credits.monthlyGrant, tokenRateOf(credits)) : null}
              format={(n) => formatTurns(n, '')}
              emptyLimitText={t('billingPage.remainingCredits', { amount: walletHeadline(credits).main })}
            />
          ) : (
            <UsageMeter label={t('billingPage.aiToday')} used={usage.aiToday} limit={usage.aiDailyLimit} emptyLimitText={t('billingPage.aiNotEnabled')} />
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function UsageMeter({
  label, hint, used, limit, emptyLimitText, format = formatNumber,
}: { label: string; hint?: string; used: number; limit: number | null; emptyLimitText?: string; format?: (n: number) => string }) {
  const { t } = useTranslation('billing')
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const danger = limit != null && used >= limit * 0.9
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="min-w-0 text-muted-foreground" title={hint}>{label}</span>
        <span className={cn('shrink-0 tabular-nums', danger && 'font-medium text-destructive')}>
          {format(used)}
          {limit != null ? ` / ${format(limit)}` : ''}
        </span>
      </div>
      {limit != null ? (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className={cn('h-full rounded-full transition-all', danger ? 'bg-destructive' : 'bg-primary')} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{emptyLimitText ?? t('billingPage.unlimited')}</p>
      )}
      {hint && limit != null && <p className="text-xs text-muted-foreground [overflow-wrap:anywhere] sm:truncate">{hint}</p>}
    </div>
  )
}

// ── Bảng gói ─────────────────────────────────────────────────────────

/** Màu thẻ gói kiểu SaleSmartly: dải màu nhạt phía trên, biểu tượng riêng cho từng gói. */
const PLAN_TONES = [
  { icon: Sprout, bg: 'from-slate-100/90 dark:from-slate-800/40', chip: 'bg-slate-500/10 text-slate-600 dark:text-slate-300' },
  { icon: Zap, bg: 'from-sky-100/90 dark:from-sky-900/30', chip: 'bg-sky-500/10 text-sky-600 dark:text-sky-300' },
  { icon: Gem, bg: 'from-orange-100/90 dark:from-orange-900/25', chip: 'bg-orange-500/10 text-orange-600 dark:text-orange-300' },
  { icon: Building2, bg: 'from-violet-100/90 dark:from-violet-900/25', chip: 'bg-violet-500/10 text-violet-600 dark:text-violet-300' },
] as const

type PlanTone = (typeof PLAN_TONES)[number]

function PlanCard({
  plan, tone, save, current, allPlans, upgradeOnly, canPay, onBuy, onContact,
}: {
  plan: PlanDef
  tone: PlanTone
  save: number
  current: ResolvedPlan
  allPlans: PricingPlan[]
  /** Gói trả phí còn hạn → gói thấp hơn không mua được ("mua rồi không trả lại"). */
  upgradeOnly: boolean
  canPay: boolean
  onBuy: () => void
  onContact: () => void
}) {
  const { t } = useTranslation('billing')
  const isCurrent = current.kind === 'known' && current.id === plan.id
  const upgrade = isUpgrade(current, plan.id, allPlans)
  // Lượt AI tặng / tháng quy theo giá trị 1 lượt (query ví dùng chung, chỉ tải một lần).
  const { data: credits } = useAiCredits()
  const rate = tokenRateOf(credits)
  const Icon = tone.icon
  const noAdmin = (
    <Button variant="outline" className="w-full" disabled title={t('addon.onlyOwnerAdmin')}>
      {t('billingPage.contactAdmin')}
    </Button>
  )

  let cta
  if (isCurrent && plan.purchasable) cta = canPay ? <Button variant="secondary" className="w-full" onClick={onBuy}>{t('billingPage.renewButton')}</Button> : noAdmin
  else if (isCurrent) cta = <Button variant="secondary" className="w-full" disabled>{t('billingPage.currentPlanButton')}</Button>
  else if (isBlockedDowngrade(upgradeOnly, current, plan.id, allPlans)) {
    cta = (
      <div className="space-y-1">
        <Button variant="outline" className="w-full" disabled title={downgradeNote()}>{t('billingPage.cannotDowngrade')}</Button>
        <p className="text-center text-xs text-muted-foreground">{downgradeNote()}</p>
      </div>
    )
  } else if (!plan.purchasable) cta = <Button variant="outline" className="w-full" onClick={onContact}>{t('billingPage.contactButton')}</Button>
  else if (upgrade) cta = canPay ? <Button className="w-full" onClick={onBuy}>{t('billingPage.upgradePlanButton')}</Button> : noAdmin
  else cta = <Button variant="outline" className="w-full" onClick={onBuy} disabled={!canPay}>{t('billingPage.switchToPlan')}</Button>

  return (
    <div
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-xl border bg-card bg-gradient-to-b to-card to-40% transition-all duration-200',
        'hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg',
        tone.bg,
        isCurrent && 'border-primary ring-1 ring-primary/30',
        plan.highlight && !isCurrent && 'border-orange-300 dark:border-orange-500/50',
      )}
    >
      {plan.purchasable && save > 0 && (
        <span className="absolute right-0 top-0 inline-flex items-center gap-1 rounded-bl-xl bg-gradient-to-r from-orange-500 to-rose-500 px-2.5 py-1 text-xs font-medium text-white">
          <BadgePercent className="h-3.5 w-3.5" /> −{save}%
        </span>
      )}
      <div className="space-y-3 p-5">
        <div className="flex items-center gap-2">
          <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', tone.chip)}>
            <Icon className="h-4 w-4" />
          </span>
          <h3 className="text-base font-semibold">{plan.name}</h3>
          {plan.highlight && <Badge className="bg-orange-500 text-white hover:bg-orange-500">{t('billingPage.popular')}</Badge>}
          {isCurrent && <Badge variant="secondary">{t('plans.inUse')}</Badge>}
        </div>
        <p className="min-h-10 text-sm text-muted-foreground">{plan.description}</p>
        <div className="flex items-baseline gap-1">
          {plan.monthlyPrice == null ? (
            <span className="text-2xl font-semibold">{t('billingPage.contact')}</span>
          ) : plan.monthlyPrice === 0 ? (
            <span className="text-2xl font-semibold">0 ₫<span className="text-sm font-normal text-muted-foreground">{t('upgrade.perMonthSuffix')}</span></span>
          ) : (
            <>
              <span className="text-2xl font-semibold tabular-nums">{formatVnd(plan.monthlyPrice)}</span>
              <span className="text-sm text-muted-foreground">{t('upgrade.perMonthSuffix')}</span>
            </>
          )}
        </div>
        {cta}
      </div>
      <div className="flex-1 space-y-3 border-t border-dashed px-5 py-4 text-sm">
        <ul className="space-y-1.5 text-muted-foreground">
          {QUOTA_KEYS.map((k) => (
            <LimitLine key={k} label={QUOTA_META[k].short}
              value={plan.monthlyPrice == null ? null : plan.included[k]}
              extra={plan.purchasable && plan.max[k] !== plan.included[k] ? t('billingPage.canBuyMore') : undefined} />
          ))}
          <LimitLine label={t('billingPage.activeContactsPerMonth')} value={plan.activeContactsPerMonth} />
          <AiLimitLine plan={plan} rate={rate} />
        </ul>
        {plan.featuresIntro && <p className="font-medium">{plan.featuresIntro}</p>}
        {plan.features.length > 0 && (
          <ul className="space-y-1.5">
            {plan.features.map((f) => (
              <li key={f} className="flex gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** "70.000 lượt AI tặng / tháng" — chưa có bộ quy đổi (chưa tải ví) thì ẩn. */
function AiLimitLine({ plan, rate }: { plan: PricingPlan; rate: TokenRate | null }) {
  const { t } = useTranslation('billing')
  const ai = planAiLimit(plan, rate)
  if (ai.label !== PLAN_AI_LABEL) return <LimitLine label={ai.label} value={ai.value} />
  if (!ai.text && ai.value != null) return null
  if (ai.value === 0) return <li className="text-muted-foreground">{t('billingPage.noAiGrant')}</li>
  return (
    <li>
      <span className="font-medium text-foreground">{ai.text ? ai.text.replace(/ lượt AI$/, '') : t('billingPage.unlimited')}</span> {ai.label}
    </li>
  )
}

function LimitLine({ label, value, extra }: { label: string; value: number | null; extra?: string }) {
  const { t } = useTranslation('billing')
  return (
    <li>
      <span className="font-medium text-foreground">{value == null ? t('billingPage.unlimited') : formatNumber(value)}</span> {label}
      {extra && <span className="ml-1 text-xs text-primary">· {extra}</span>}
    </li>
  )
}

// ── Tiện ích bổ sung ────────────────────────────────────────────────

function AddonsSection({
  summary, onBuy, canAddon, canPay, onAddon,
}: { summary: BillingSummary; onBuy: (target: string) => void; canAddon: boolean; canPay: boolean; onAddon: () => void }) {
  const { t } = useTranslation('billing')
  const { aiToday, aiDailyLimit } = summary.usage
  const remaining = aiDailyLimit != null ? Math.max(0, aiDailyLimit - aiToday) : null
  const { data: credits } = useAiCredits()
  const canBuyCredits = useCanBuyAiCredits()
  const [buyCredits, setBuyCredits] = useState(false)
  const walletMeta = credits ? WALLET_STATUS_META[deriveWalletStatus(credits)] : null
  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium">{t('billingPage.addonsTitle')}</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="flex h-full flex-col gap-3 p-5">
            <div className="flex items-center gap-2 font-medium">
              <Users className="h-4 w-4 text-primary" /> {t('billingPage.membersAndChannels')}
            </div>
            <p className="text-sm text-muted-foreground">
              {canAddon
                ? t('billingPage.addonCardDescEnabled')
                : t('billingPage.addonCardDescDisabled')}
            </p>
            {summary.addonPurchased && (
              <p className="text-xs text-muted-foreground">{t('billingPage.hasAddon', { delta: addonDeltaText(summary.addonPurchased) })}</p>
            )}
            <div className="mt-auto">
              {canAddon ? (
                <Button onClick={onAddon} disabled={!canPay} title={canPay ? undefined : t('addon.onlyOwnerAdmin')}>
                  <PlusCircle /> {t('billingPage.buyMoreButton')}
                </Button>
              ) : (
                <Button asChild><Link to="/billing/upgrade">{t('addon.choosePlanOrRenew')}</Link></Button>
              )}
            </div>
          </CardContent>
        </Card>
        {credits && walletMeta ? (
          <Card>
            <CardContent className="flex h-full flex-col gap-3 p-5">
              <div className="flex items-center gap-2 font-medium">
                <Bot className="h-4 w-4 text-primary" /> {t('billingPage.aiTurns')}
                <Badge variant={walletMeta.tone} className="ml-auto">{walletMeta.label}</Badge>
              </div>
              <p className="text-sm">
                {t('billingPage.remaining')} <span className="font-medium tabular-nums">{walletHeadline(credits).main}</span>
                <span className="block text-muted-foreground">{t('billingPage.aiBalanceDetail', { grant: amountText(credits.grantBalance, tokenRateOf(credits)), paid: amountText(credits.paidBalance, tokenRateOf(credits)) })}</span>
              </p>
              {credits.monthlyGrant > 0 && (
                <UsageMeter
                  label={t('billingPage.usedThisMonth')} format={(n) => formatTurns(n, '')}
                  used={unitsToTurns(credits.usedThisMonth, tokenRateOf(credits))} limit={unitsToTurns(credits.monthlyGrant, tokenRateOf(credits))}
                />
              )}
              <p className="text-sm text-muted-foreground">
                {t('billingPage.aiUsageExplainer')}
              </p>
              <div className="mt-auto flex items-center gap-2">
                {canBuyCredits && (
                  <Button onClick={() => setBuyCredits(true)}>
                    <PlusCircle /> {t('billingPage.buyAiTurns')}
                  </Button>
                )}
                <Button variant="ghost" size="sm" asChild><Link to="/ai?tab=usage">{t('billingPage.viewHistory')}</Link></Button>
              </div>
              <BuyCreditsDialog open={buyCredits} onOpenChange={setBuyCredits} />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex h-full flex-col gap-3 p-5">
              <div className="flex items-center gap-2 font-medium">
                <Bot className="h-4 w-4 text-primary" /> {t('billingPage.aiTurnsToday')}
              </div>
              {aiDailyLimit != null ? (
                <>
                  <UsageMeter label={t('billingPage.usedToday')} used={aiToday} limit={aiDailyLimit} />
                  <p className="text-sm text-muted-foreground">
                    {t('billingPage.remainingTurns', { count: formatNumber(remaining ?? 0) })}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{t('billingPage.aiNotUsedYet')}</p>
              )}
              <div className="mt-auto">
                <Button onClick={() => onBuy(t('billingPage.moreDailyAiTurns'))}>{t('billingPage.buyMoreButton')}</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  )
}

// ── Liên hệ nâng cấp ────────────────────────────────────────────────

function ContactDialog({ target, currentName, onClose }: { target: string | null; currentName: string; onClose: () => void }) {
  const { t } = useTranslation('billing')
  const brand = getBranding()
  const orgName = useAuthStore((s) => s.user?.org?.name)
  const request = t('billingPage.contactRequestText', { brand: brand.brandName, org: orgName ?? '—', plan: currentName, target: target ?? '' })

  const copy = () => {
    navigator.clipboard.writeText(request).then(
      () => toast.success(t('billingPage.toastCopied')),
      () => toast.error(t('billingPage.toastCopyFailed')),
    )
  }

  return (
    <Dialog open={target != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('billingPage.contactDialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('billingPage.contactDialogDesc', { brand: brand.brandName })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>
            {t('billingPage.contactDialogBodyPrefix')} <span className="font-medium">{target}</span>{t('billingPage.contactDialogBodySuffix')}
          </p>
          <pre className="whitespace-pre-wrap rounded-lg bg-muted p-3 font-sans text-xs text-muted-foreground">{request}</pre>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copy}><Copy /> {t('billingPage.copyRequestButton')}</Button>
          <Button onClick={onClose}>{t('billingPage.gotIt')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
